import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  Users,
  User,
  Calendar,
  Download,
  Upload,
  Settings,
  Bell,
  CheckCircle,
  AlertCircle,
  Clock,
  Star,
  Crown,
  Zap,
  Shield,
  BarChart3,
  PieChart,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Plus,
  Edit3,
  Trash2,
  Eye,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ExternalLink,
  FileText,
  Mail,
  Phone,
  Globe,
  Lock,
  Unlock,
  Filter,
  Search,
  X,
  Check,
  Sparkles,
  Target,
  Award,
} from "lucide-react";

import "../../../styles/modern-design-system.css";

const CRMSubscriptionManagement = () => {
  // State Management
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState({});
  const [billing, setBilling] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [filters, setFilters] = useState({
    period: "current",
    status: "all",
    type: "all",
  });
  const [analytics, setAnalytics] = useState({
    monthlyRevenue: 0,
    totalSubscribers: 0,
    churnRate: 0,
    avgRevenuePerUser: 0,
    usageGrowth: 0,
    billingEfficiency: 0,
  });

  // Subscription plans configuration
  const subscriptionPlans = [
    {
      id: "itinerary_starter",
      name: "Itinerary Starter",
      price: 147,
      period: "month",
      description: "Perfect for solo travel agents starting out",
      features: [
        "Up to 5 itineraries per month",
        "Basic client management",
        "Email support",
        "Standard templates",
      ],
      limits: {
        itineraries: 5,
        clients: 50,
        agents: 1,
      },
      color: "blue",
      icon: Target,
      popular: false,
      badge: null,
    },
    {
      id: "basic_crm",
      name: "Basic CRM",
      price: 167,
      period: "month",
      description: "Essential CRM features for growing agents",
      features: [
        "Up to 7 itineraries per month",
        "Basic CRM functionality",
        "Client pipeline tracking",
        "Email support",
        "Basic reporting",
      ],
      limits: {
        itineraries: 7,
        clients: 100,
        agents: 1,
      },
      color: "green",
      icon: Users,
      popular: false,
      badge: "Decoy",
    },
    {
      id: "pro",
      name: "Pro",
      price: 197,
      period: "month",
      description: "Complete solution for professional travel agents",
      features: [
        "Up to 10 itineraries per month",
        "Full CRM access",
        "Automations & workflows",
        "Client portal",
        "Payment links",
        "Email campaigns",
        "Advanced reporting",
        "Priority support",
      ],
      limits: {
        itineraries: 10,
        clients: 500,
        agents: 3,
      },
      color: "purple",
      icon: Crown,
      popular: true,
      badge: "Best Value",
    },
    {
      id: "agency_crm",
      name: "Agency CRM",
      price: 247,
      period: "month",
      description: "Advanced features for travel agencies",
      features: [
        "Up to 12 itineraries per month",
        "Commission tracking",
        "Advanced reporting",
        "Team collaboration",
        "Custom workflows",
        "API access",
        "Dedicated support",
      ],
      limits: {
        itineraries: 12,
        clients: 1000,
        agents: 5,
      },
      color: "orange",
      icon: Users,
      popular: false,
      badge: "Decoy",
    },
    {
      id: "agency_plus",
      name: "Agency+",
      price: 297,
      period: "month",
      description: "Premium solution for large agencies",
      features: [
        "Up to 20 itineraries per month",
        "Advanced CRM features",
        "Custom branding",
        "Advanced workflows",
        "Team collaboration",
        "Full API access",
        "White-label options",
        "Dedicated account manager",
      ],
      limits: {
        itineraries: 20,
        clients: 2500,
        agents: 10,
      },
      color: "gold",
      icon: Award,
      popular: false,
      badge: "Recommended for Agencies",
    },
  ];

  // Simulate data loading
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock current subscription data
      const mockCurrentSubscription = {
        id: "sub_1234567890",
        planId: "pro",
        planName: "Pro",
        status: "active",
        currentPeriodStart: "2024-01-01T00:00:00Z",
        currentPeriodEnd: "2024-02-01T00:00:00Z",
        cancelAtPeriodEnd: false,
        trialEnd: null,
        price: 197,
        currency: "USD",
        interval: "month",
        nextBillingDate: "2024-02-01T00:00:00Z",
        autoRenew: true,
        createdAt: "2024-01-01T00:00:00Z",
      };

      // Mock usage data
      const mockUsage = {
        itineraries: {
          used: 7,
          limit: 10,
          percentage: 70,
        },
        clients: {
          used: 127,
          limit: 500,
          percentage: 25,
        },
        agents: {
          used: 2,
          limit: 3,
          percentage: 67,
        },
        apiCalls: {
          used: 15420,
          limit: 50000,
          percentage: 31,
        },
        storage: {
          used: 2.3, // GB
          limit: 10, // GB
          percentage: 23,
        },
      };

      // Mock billing data
      const mockBilling = {
        currentBalance: 197.00,
        nextBillingDate: "2024-02-01",
        paymentMethod: {
          type: "card",
          last4: "4242",
          brand: "visa",
          expMonth: 12,
          expYear: 2025,
        },
        billingHistory: [
          {
            id: "in_1234567890",
            date: "2024-01-01",
            amount: 197.00,
            status: "paid",
            description: "Pro Plan - January 2024",
            invoiceUrl: "https://billing.stripe.com/invoice/1234567890",
          },
          {
            id: "in_0987654321",
            date: "2023-12-01",
            amount: 197.00,
            status: "paid",
            description: "Pro Plan - December 2023",
            invoiceUrl: "https://billing.stripe.com/invoice/0987654321",
          },
        ],
        upcomingCharges: [
          {
            date: "2024-02-01",
            amount: 197.00,
            description: "Pro Plan - February 2024",
          },
        ],
      };

      // Mock analytics
      const mockAnalytics = {
        monthlyRevenue: 12540,
        totalSubscribers: 89,
        churnRate: 2.3,
        avgRevenuePerUser: 197,
        usageGrowth: 15.2,
        billingEfficiency: 98.5,
      };

      setCurrentSubscription(mockCurrentSubscription);
      setUsage(mockUsage);
      setBilling(mockBilling);
      setAnalytics(mockAnalytics);
      
      setLoading(false);
    };

    loadData();
  }, []);

  // Get current plan details
  const currentPlan = useMemo(() => {
    if (!currentSubscription) return null;
    return subscriptionPlans.find(plan => plan.id === currentSubscription.planId);
  }, [currentSubscription]);

  // Handle plan upgrade/downgrade
  const handlePlanChange = (newPlanId) => {
    setSelectedPlan(subscriptionPlans.find(plan => plan.id === newPlanId));
    setShowUpgradeModal(true);
  };

  // Handle subscription cancellation
  const handleCancelSubscription = () => {
    // Implement cancellation logic
    console.log("Cancel subscription");
  };

  // Handle payment method update
  const handleUpdatePaymentMethod = () => {
    // Implement payment method update logic
    console.log("Update payment method");
  };

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-subscription-loading">
        <div className="crm-loading-spinner">
          <RefreshCw className="animate-spin" size={48} />
          <p className="crm-loading-text">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="crm-main-content"
    >

      {/* Current Subscription Overview */}
      <motion.div variants={itemVariants} className="crm-current-subscription">
        <div className="crm-subscription-card">
          <div className="crm-subscription-header">
            <div className="crm-subscription-info">
              <div className="crm-subscription-icon">
                {currentPlan && <currentPlan.icon size={32} />}
              </div>
              <div>
                <h2 className="crm-subscription-name">
                  {currentSubscription?.planName} Plan
                </h2>
                <p className="crm-subscription-description">
                  {currentPlan?.description}
                </p>
              </div>
            </div>
            <div className="crm-subscription-status">
              <div className={`crm-status-badge crm-status-${currentSubscription?.status}`}>
                <CheckCircle size={16} />
                <span>{currentSubscription?.status}</span>
              </div>
            </div>
          </div>

          <div className="crm-subscription-details">
            <div className="crm-subscription-meta">
              <div className="crm-meta-item">
                <Calendar size={16} />
                <span>Next billing: {formatDate(currentSubscription?.nextBillingDate)}</span>
              </div>
              <div className="crm-meta-item">
                <DollarSign size={16} />
                <span>{formatCurrency(currentSubscription?.price)}/{currentSubscription?.interval}</span>
              </div>
              <div className="crm-meta-item">
                <CreditCard size={16} />
                <span>**** **** **** {billing?.paymentMethod?.last4}</span>
              </div>
            </div>

            <div className="crm-subscription-actions">
              <button
                className="crm-action-btn crm-action-secondary"
                onClick={handleUpdatePaymentMethod}
              >
                <CreditCard size={16} />
                <span>Update Payment</span>
              </button>
              <button
                className="crm-action-btn crm-action-secondary"
                onClick={() => setShowBillingHistory(true)}
              >
                <FileText size={16} />
                <span>Billing History</span>
              </button>
              <button
                className="crm-action-btn crm-action-primary"
                onClick={() => setShowUpgradeModal(true)}
              >
                <ArrowUpRight size={16} />
                <span>Upgrade Plan</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Analytics Cards */}
      <motion.div variants={itemVariants} className="crm-analytics-grid">
        <div className="crm-analytics-card crm-card-blue">
          <div className="crm-analytics-icon">
            <DollarSign size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{formatCurrency(analytics.monthlyRevenue)}</div>
            <div className="crm-analytics-label">Monthly Revenue</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-green">
          <div className="crm-analytics-icon">
            <Users size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{analytics.totalSubscribers}</div>
            <div className="crm-analytics-label">Active Subscribers</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-purple">
          <div className="crm-analytics-icon">
            <TrendingUp size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{analytics.usageGrowth}%</div>
            <div className="crm-analytics-label">Usage Growth</div>
          </div>
        </div>
        <div className="crm-analytics-card crm-card-orange">
          <div className="crm-analytics-icon">
            <BarChart3 size={24} />
          </div>
          <div className="crm-analytics-content">
            <div className="crm-analytics-value">{analytics.billingEfficiency}%</div>
            <div className="crm-analytics-label">Billing Efficiency</div>
          </div>
        </div>
      </motion.div>

      {/* Usage Tracking */}
      <motion.div variants={itemVariants} className="crm-usage-section">
        <div className="crm-section-header">
          <h3 className="crm-section-title">Usage This Month</h3>
          <button
            className="crm-action-btn crm-action-secondary"
            onClick={() => setShowUsageDetails(true)}
          >
            <BarChart3 size={16} />
            <span>View Details</span>
          </button>
        </div>

        <div className="crm-usage-grid">
          {Object.entries(usage).map(([key, data]) => {
            const usageLabels = {
              itineraries: "Itineraries",
              clients: "Clients",
              agents: "Agents",
              apiCalls: "API Calls",
              storage: "Storage (GB)",
            };

            return (
              <div key={key} className="crm-usage-card">
                <div className="crm-usage-header">
                  <h4 className="crm-usage-title">{usageLabels[key]}</h4>
                  <div className="crm-usage-percentage">{data.percentage}%</div>
                </div>
                <div className="crm-usage-bar">
                  <div
                    className={`crm-usage-progress crm-usage-${data.percentage > 80 ? 'warning' : data.percentage > 60 ? 'info' : 'success'}`}
                    style={{ width: `${Math.min(data.percentage, 100)}%` }}
                  />
                </div>
                <div className="crm-usage-details">
                  <span className="crm-usage-used">{data.used.toLocaleString()}</span>
                  <span className="crm-usage-separator">/</span>
                  <span className="crm-usage-limit">{data.limit.toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Available Plans */}
      <motion.div variants={itemVariants} className="crm-plans-section">
        <div className="crm-section-header">
          <h3 className="crm-section-title">Available Plans</h3>
          <p className="crm-section-subtitle">
            Choose the plan that fits your business needs
          </p>
        </div>

        <div className="crm-plans-grid">
          {subscriptionPlans.map((plan) => {
            const PlanIcon = plan.icon;
            const isCurrentPlan = currentSubscription?.planId === plan.id;
            const isUpgrade = plan.price > (currentSubscription?.price || 0);
            const isDowngrade = plan.price < (currentSubscription?.price || 0);

            return (
              <motion.div
                key={plan.id}
                variants={itemVariants}
                className={`crm-plan-card crm-plan-${plan.color} ${isCurrentPlan ? 'current' : ''} ${plan.popular ? 'popular' : ''}`}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {/* Plan Badge */}
                {plan.badge && (
                  <div className={`crm-plan-badge crm-badge-${plan.color}`}>
                    <span>{plan.badge}</span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="crm-plan-header">
                  <div className="crm-plan-icon">
                    <PlanIcon size={32} />
                  </div>
                  <div className="crm-plan-info">
                    <h4 className="crm-plan-name">{plan.name}</h4>
                    <p className="crm-plan-description">{plan.description}</p>
                  </div>
                </div>

                {/* Plan Pricing */}
                <div className="crm-plan-pricing">
                  <div className="crm-plan-price">
                    <span className="crm-price-currency">$</span>
                    <span className="crm-price-amount">{plan.price}</span>
                    <span className="crm-price-period">/{plan.period}</span>
                  </div>
                </div>

                {/* Plan Features */}
                <div className="crm-plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="crm-feature-item">
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Plan Limits */}
                <div className="crm-plan-limits">
                  <div className="crm-limit-item">
                    <Target size={14} />
                    <span>{plan.limits.itineraries} itineraries/month</span>
                  </div>
                  <div className="crm-limit-item">
                    <Users size={14} />
                    <span>{plan.limits.clients} clients</span>
                  </div>
                  <div className="crm-limit-item">
                    <User size={14} />
                    <span>{plan.limits.agents} agents</span>
                  </div>
                </div>

                {/* Plan Action */}
                <div className="crm-plan-action">
                  {isCurrentPlan ? (
                    <button className="crm-action-btn crm-action-current" disabled>
                      <CheckCircle size={16} />
                      <span>Current Plan</span>
                    </button>
                  ) : (
                    <button
                      className={`crm-action-btn ${isUpgrade ? 'crm-action-primary' : 'crm-action-secondary'}`}
                      onClick={() => handlePlanChange(plan.id)}
                    >
                      {isUpgrade ? (
                        <>
                          <ArrowUpRight size={16} />
                          <span>Upgrade</span>
                        </>
                      ) : isDowngrade ? (
                        <>
                          <ArrowDownRight size={16} />
                          <span>Downgrade</span>
                        </>
                      ) : (
                        <>
                          <ArrowUpRight size={16} />
                          <span>Select Plan</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Billing Information */}
      <motion.div variants={itemVariants} className="crm-billing-section">
        <div className="crm-section-header">
          <h3 className="crm-section-title">Billing Information</h3>
          <button
            className="crm-action-btn crm-action-secondary"
            onClick={handleUpdatePaymentMethod}
          >
            <Edit3 size={16} />
            <span>Update</span>
          </button>
        </div>

        <div className="crm-billing-grid">
          <div className="crm-billing-card">
            <div className="crm-billing-header">
              <CreditCard size={20} />
              <h4>Payment Method</h4>
            </div>
            <div className="crm-billing-content">
              <div className="crm-payment-method">
                <div className="crm-card-icon">
                  <CreditCard size={24} />
                </div>
                <div className="crm-card-details">
                  <div className="crm-card-brand">
                    {billing?.paymentMethod?.brand?.toUpperCase()}
                  </div>
                  <div className="crm-card-number">
                    **** **** **** {billing?.paymentMethod?.last4}
                  </div>
                  <div className="crm-card-expiry">
                    Expires {billing?.paymentMethod?.expMonth}/{billing?.paymentMethod?.expYear}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="crm-billing-card">
            <div className="crm-billing-header">
              <Calendar size={20} />
              <h4>Next Billing</h4>
            </div>
            <div className="crm-billing-content">
              <div className="crm-billing-date">
                {formatDate(currentSubscription?.nextBillingDate)}
              </div>
              <div className="crm-billing-amount">
                {formatCurrency(currentSubscription?.price)}
              </div>
              <div className="crm-billing-auto-renew">
                <CheckCircle size={16} />
                <span>Auto-renewal enabled</span>
              </div>
            </div>
          </div>

          <div className="crm-billing-card">
            <div className="crm-billing-header">
              <DollarSign size={20} />
              <h4>Billing History</h4>
            </div>
            <div className="crm-billing-content">
              <div className="crm-billing-summary">
                <div className="crm-billing-stat">
                  <span className="crm-stat-label">Total Paid</span>
                  <span className="crm-stat-value">
                    {formatCurrency(billing?.currentBalance * 12)}
                  </span>
                </div>
                <div className="crm-billing-stat">
                  <span className="crm-stat-label">Last Payment</span>
                  <span className="crm-stat-value">
                    {billing?.billingHistory?.[0]?.date}
                  </span>
                </div>
              </div>
              <button
                className="crm-action-btn crm-action-secondary"
                onClick={() => setShowBillingHistory(true)}
              >
                <FileText size={16} />
                <span>View All</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Plan Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="crm-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="crm-modal-content"
            >
              <div className="crm-modal-header">
                <h3 className="crm-modal-title">Upgrade to {selectedPlan.name}</h3>
                <button
                  className="crm-close-btn"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="crm-modal-body">
                <div className="crm-upgrade-summary">
                  <div className="crm-upgrade-plan">
                    <div className="crm-plan-icon">
                      <selectedPlan.icon size={32} />
                    </div>
                    <div>
                      <h4>{selectedPlan.name}</h4>
                      <p>{selectedPlan.description}</p>
                    </div>
                  </div>
                  <div className="crm-upgrade-pricing">
                    <span className="crm-price">{formatCurrency(selectedPlan.price)}</span>
                    <span className="crm-period">/{selectedPlan.period}</span>
                  </div>
                </div>

                <div className="crm-upgrade-features">
                  <h5>What's included:</h5>
                  <ul>
                    {selectedPlan.features.map((feature, index) => (
                      <li key={index}>
                        <CheckCircle size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="crm-upgrade-changes">
                  <h5>Changes to your subscription:</h5>
                  <div className="crm-change-item">
                    <span>New billing amount:</span>
                    <span className="crm-change-value">
                      {formatCurrency(selectedPlan.price)}/{selectedPlan.period}
                    </span>
                  </div>
                  <div className="crm-change-item">
                    <span>Effective date:</span>
                    <span className="crm-change-value">
                      {formatDate(currentSubscription?.nextBillingDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="crm-modal-actions">
                <button
                  className="crm-action-btn crm-action-secondary"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Cancel
                </button>
                <button className="crm-action-btn crm-action-primary">
                  <CreditCard size={16} />
                  <span>Upgrade Now</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CRMSubscriptionManagement;
